import { useTranslation } from 'react-i18next';
import Select, { SelectComponents } from '@lib/components/Select/Select';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import SectorOption from '@lib/components/Select/components/SectorOption';
import SectorSingleValue from '@lib/components/Select/components/SectorSingleValue';
import SiteOption from '@lib/components/Select/components/SiteOption';
import { GetLoadOptions, LoadOptions } from '@lib/components/Select/types';
import { FieldItem } from '@lib/interfaces/form';
import { getOptionsArrayValidation } from '@lib/utils/validationUtils';
import getValidationStatusOptions from '@lib/utils/documentation/getValidationStatusOptions';

export const DocumentFilterFieldNames = {
  Categories: 'categories',
  ValidationStatus: 'validationStatus',
  Sectors: 'sectors',
  Sites: 'sites',
  Areas: 'areas',
  Equipment: 'equipment',
} as const;

export const DOCUMENT_LIST_FILTER_EMPTY_VALUES: Record<string, unknown[]> = {
  [DocumentFilterFieldNames.Categories]: [],
  [DocumentFilterFieldNames.ValidationStatus]: [],
  [DocumentFilterFieldNames.Sectors]: [],
  [DocumentFilterFieldNames.Sites]: [],
  [DocumentFilterFieldNames.Areas]: [],
  [DocumentFilterFieldNames.Equipment]: [],
};

type Props = {
  getCategoriesLoadOptions: LoadOptions;
  getSectorsLoadOptions: LoadOptions;
  getSitesLoadOptions: LoadOptions;
  getAreasLoadOptions: LoadOptions;
  getEquipmentLoadOptions: LoadOptions;
};

const multiSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
} as unknown as SelectComponents;

const sectorsSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
  Option: SectorOption,
  SingleValue: SectorSingleValue,
} as unknown as SelectComponents;

const sitesSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
  Option: SiteOption,
} as unknown as SelectComponents;

export default function useDocumentFilterFields({
  getCategoriesLoadOptions,
  getSectorsLoadOptions,
  getSitesLoadOptions,
  getAreasLoadOptions,
  getEquipmentLoadOptions,
}: Props): FieldItem[] {
  const { t } = useTranslation();
  const validationStatusOptions = getValidationStatusOptions().map(
    ({ value, labelKey }) => ({ value, label: t(labelKey) }),
  );
  return [
    {
      name: DocumentFilterFieldNames.Categories,
      label: t('categories-label'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        defaultOptions: false,
        isAsync: true,
        isMulti: true,
        isClearable: true,
        getLoadOptions: (() => getCategoriesLoadOptions) as GetLoadOptions,
        maxMenuHeight: 200,
        usePortal: true,
        components: multiSelectComponents,
      },
      isOptionalFn: () => false,
    },
    {
      name: DocumentFilterFieldNames.ValidationStatus,
      label: t('validation-status'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        options: validationStatusOptions,
        isMulti: true,
        isSearchable: false,
        isClearable: true,
        maxMenuHeight: 200,
        usePortal: true,
        components: multiSelectComponents,
      },
      isOptionalFn: () => false,
    },
    {
      name: DocumentFilterFieldNames.Sectors,
      label: t('sectors-label'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        defaultOptions: false,
        isAsync: true,
        isMulti: true,
        isClearable: true,
        getLoadOptions: (() => getSectorsLoadOptions) as GetLoadOptions,
        maxMenuHeight: 200,
        usePortal: true,
        components: sectorsSelectComponents,
      },
      isOptionalFn: () => false,
    },
    {
      name: DocumentFilterFieldNames.Sites,
      label: t('sites-label'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        defaultOptions: false,
        isAsync: true,
        isMulti: true,
        isClearable: true,
        getLoadOptions: (() => getSitesLoadOptions) as GetLoadOptions,
        maxMenuHeight: 200,
        usePortal: true,
        components: sitesSelectComponents,
      },
      isOptionalFn: () => false,
    },
    {
      name: DocumentFilterFieldNames.Areas,
      label: t('areas-label'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        defaultOptions: false,
        isAsync: true,
        isMulti: true,
        isClearable: true,
        getLoadOptions: (() => getAreasLoadOptions) as GetLoadOptions,
        maxMenuHeight: 200,
        usePortal: true,
        components: multiSelectComponents,
      },
      isOptionalFn: () => false,
    },
    {
      name: DocumentFilterFieldNames.Equipment,
      label: t('assets'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        defaultOptions: false,
        isAsync: true,
        isMulti: true,
        isClearable: true,
        getLoadOptions: (() => getEquipmentLoadOptions) as GetLoadOptions,
        maxMenuHeight: 200,
        usePortal: true,
        components: multiSelectComponents,
      },
      isOptionalFn: () => false,
    },
  ];
}
